import styled, { css, keyframes } from "styled-components";

import { getSpacing } from "@braze/beacon-styling";

/**
 * @note This has to stay in sync with the &.block-interaction rule in
 * dashboard/app/assets/less/dashboard/bootstrap-module-overrides.less
 * while we lean on ko-interop for the step edit modality
 */
const TOP_OFFSET = 45;

/**
 * How long we want the open / close projection animation to last in milliseconds.
 */
export const MODAL_EDIT_TRANSITION = 200;

/**
 * @note This should stay in sync with:
 * dashboard/app/assets/less/dashboard.workflows.less
 */
const FIXED_MODAL_RIGHT_SIDE_WIDTH = 1023;

/**
 * Width of a step body in the canvas grid. This would ideally be an imported
 * constant that lives inside of CanvasGrid, but it's re-defined here until
 * that's easier to do.
 */
const STEP_BODY_WIDTH = 200;

/**
 * The sum of STEP_BODY_WIDTH, FIXED_MODAL_RIGHT_SIDE_WIDTH, and the width of
 * the margin between them (currently getSpacing("sm") or 12px).
 */
export const FIXED_MODAL_TOTAL_WIDTH = FIXED_MODAL_RIGHT_SIDE_WIDTH + STEP_BODY_WIDTH + 12;

/**
 * Animation to fade in the modal on mount. This is necessary because an
 * initial render with opacity=1 will just make the modal appear without the
 * transition firing (because it was never rendered at opacity 0, there's
 * nothing to "transition" from).
 */
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/**
 * Fixed div that occupies the entire screen with a semi-transparent
 * white background and fades in on render. The modal is a child of this
 * element, so this effectively controls the animations for the whole modal.
 */
export const ModalBackdrop = styled.div<{ skipAnimation?: boolean; zIndex: number; centered?: boolean }>`
  position: fixed;
  width: calc(100vw - var(--operator-inline-width, 0px));
  height: 100vh;
  top: 0;
  left: 0;
  display: flex;
  ${({ centered = true }) =>
    centered
      ? `
        justify-content: center;
      `
      : `
        justify-content: flex-start;
      `}
  overflow: hidden;
  z-index: ${(props) => props.zIndex};

  opacity: 1;
  pointer-events: auto;
  background-color: rgba(255, 255, 255, 0.9);
  ${(props) =>
    !props.skipAnimation &&
    css`
      transition: opacity ${MODAL_EDIT_TRANSITION}ms;
      animation: ${fadeIn} ease ${MODAL_EDIT_TRANSITION}ms;
    `}
`;

/**
 * The inner container for the modal contents (which are fixed width) that
 * actually gets scrollbars attached if the window gets too small.
 */
export const ScrollContainer = styled.div`
  overflow: auto;
  height: 100%;
  position: relative;
`;

/**
 * The fixed-width container that sits inside of ScrollContainer. Has two
 * children/columns; one for the step body + done button, and one for the
 * editor.
 */
export const ModalContent = styled.div`
  padding: ${TOP_OFFSET}px ${getSpacing("sm")} ${getSpacing("sm")};
  min-width: ${FIXED_MODAL_TOTAL_WIDTH}px;
  display: flex;
`;

/**
 * Wrapper around the step / done button column that constrains its width.
 */
export const LeftColumn = styled.div`
  width: ${STEP_BODY_WIDTH}px;
`;

/**
 * Wrapper for the child content (which should be a step body) to space it
 * from the Done button beneath it
 */
export const StepSpacer = styled.div`
  margin-bottom: ${getSpacing("sm")};
`;

/**
 * Holds the composer rendered beside the step. Stays transparent until the composer prop is
 * populated so it does not flash in empty while the composer loads.
 */
export const RightColumn = styled.div<{ hasContent: boolean }>`
  width: ${FIXED_MODAL_RIGHT_SIDE_WIDTH}px;
  margin-left: ${getSpacing("md")};
  opacity: ${(props) => (props.hasContent ? 1 : 0)};
`;

export const LeaveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${getSpacing("sm")};
`;
